import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, ShieldAlert, Trash2 } from "lucide-react";
import { Button, IconButton, Input } from "@akuna/ui";
import { useToast } from "@/hooks/use-toast";
import {
  useCreatePetAlert,
  useDeactivatePetAlert,
  useReactivatePetAlert,
  useUpdatePetAlert,
} from "../usePetProfileMutations";
import { usePetProfileUndo } from "../usePetProfileUndo";
import type { PetCustomAlert } from "../usePetReservationSummary";
import { petAlertColorPresentation } from "../petProfileModel";
import { getPetAlertText } from "../petProfileReadModel";
import { AlertColorPicker } from "../AlertColorPicker";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

export type PetAlertsSectionProps = {
  petId: string;
  customerId: string | null;
  alerts: PetCustomAlert[];
  compact?: boolean;
  showEmpty?: boolean;
};

export function PetAlertsSection({ petId, customerId, alerts, compact, showEmpty }: PetAlertsSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const createAlert = useCreatePetAlert();
  const updateAlert = useUpdatePetAlert();
  const deactivateAlert = useDeactivatePetAlert();
  const reactivateAlert = useReactivatePetAlert();
  const { removeWithUndo } = usePetProfileUndo();
  const [isEditing, setIsEditing] = useState(false);
  const visibleAlerts = alerts.filter((alert) => getPetAlertText(alert));

  if (!isEditing && visibleAlerts.length === 0 && !showEmpty) return null;

  const fail = () => toast({ title: t("petProfile.saveError"), variant: "destructive" });

  const persist = async (alert: PetCustomAlert, patch: Partial<Pick<PetCustomAlert, "value" | "color">>) => {
    try {
      await updateAlert.mutateAsync({
        id: alert.id,
        petId,
        label: alert.label,
        value: patch.value ?? alert.value,
        color: patch.color !== undefined ? patch.color : alert.color,
      });
    } catch {
      fail();
    }
  };

  const add = async () => {
    if (!customerId) return;
    try {
      await createAlert.mutateAsync({
        petId,
        customerId,
        label: t("petProfile.customAlert"),
        value: "",
        icon: null,
        color: null,
      });
    } catch {
      fail();
    }
  };

  const remove = (alert: PetCustomAlert) =>
    removeWithUndo({
      message: t("petProfile.alertRemoved"),
      remove: () => deactivateAlert.mutateAsync({ id: alert.id, petId }),
      restore: () => reactivateAlert.mutateAsync({ id: alert.id, petId }),
    });

  return (
    <PetProfileSectionShell
      title={t("petProfile.alerts")}
      icon={ShieldAlert}
      tone="danger"
      count={isEditing ? alerts.length : visibleAlerts.length}
      isEditing={isEditing}
      onToggleEdit={() => setIsEditing((current) => !current)}
      editLabel={t("petProfile.editAlerts")}
      doneLabel={t("petProfile.doneAlerts")}
      compact={compact && !isEditing}
    >
      {isEditing ? alerts.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.alertsEmpty")}</PetProfileSectionEmpty>
      ) : (
        <ul className="grid gap-2">
          {alerts.map((alert) => (
            <li key={alert.id} className="flex flex-col md:flex-row md:items-center gap-2">
              <AlertColorPicker
                value={alert.color}
                onChange={(color) => void persist(alert, { color })}
                label={t("petProfile.alertColor")}
              />
              <div className="flex items-center gap-2">
                <Input
                  aria-label={t("petProfile.alertText")}
                  className="min-w-0 flex-1"
                  defaultValue={alert.value}
                  placeholder={t("petProfile.alertPlaceholder")}
                  onBlur={(event) => {
                    const value = event.target.value;
                    if (value === alert.value) return;
                    void persist(alert, { value });
                  }}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground hover:text-state-danger"
                  label={t("petProfile.removeAlert")}
                  onClick={() => void remove(alert)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </IconButton>
              </div>
              <div className="border-t md:hidden" />
            </li>
          ))}
          </ul>
      ) : visibleAlerts.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.alertsEmpty")}</PetProfileSectionEmpty>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {visibleAlerts.flatMap((alert) => {
            const text = getPetAlertText(alert);
            if (!text) return [];
            const presentation = petAlertColorPresentation(alert.color);
            const colorClass = {
              orange: "border-orange-200 bg-orange-50 text-orange-700",
              amber: "border-amber-200 bg-amber-50 text-amber-800",
              blue: "border-blue-200 bg-blue-50 text-blue-700",
              purple: "border-purple-200 bg-purple-50 text-purple-700",
              green: "border-green-200 bg-green-50 text-green-700",
            }[alert.color ?? ""] ?? "border-state-danger/20 bg-state-danger-soft text-state-danger";
            return [<span key={alert.id} style={presentation.customColor ? { borderColor: presentation.customColor, color: presentation.customColor } : undefined} className={`max-w-full rounded-full border px-2 py-1 text-xs font-medium ${colorClass}`}>{text}</span>];
          })}
        </div>
      )}

      {isEditing ? <Button
        variant="outline"
        size="sm"
        className="w-full border-dashed mt-2"
        disabled={!customerId}
        onClick={() => void add()}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t("petProfile.addAlert")}
      </Button> : null}
    </PetProfileSectionShell>
  );
}
